import { AxiosError } from 'axios';
import { Namespace, TFunction } from 'react-i18next';

import { IErrorResponse } from '@app/models/error';
import {
  PermissionScope,
  RetrieveVisibilityRuleSchema,
  VisibilityCondition,
  VisibilityRuleSchema,
} from '@app/models/users';

import { ATTR_META } from '@pages/Employees/data/constants';
import { AttrValueType, RuleValue, VisibilityState } from '@pages/Employees/interfaces';
import { isKnownAttribute } from '@pages/Employees/services';

export const SELF_FORBIDDEN_CODE = 'assignment_self_change_forbidden';

/** Базовая роль есть у каждого сотрудника, назначать её повторно нечем. */
export const BASELINE_ROLE_CODE = 'basic_employee';

export interface ParsedAssignmentError {
  /** Ошибки по полям верхнего уровня: valid_from, valid_to, role_id, ... */
  fieldErrors: Record<string, string>;
  /** Ошибки правила видимости (вложенные указатели /visibility_rule/...). */
  ruleErrors: string[];
  /** Прочие сообщения. */
  generalErrors: string[];
  /** Попытка изменить собственное назначение (403). */
  isSelfForbidden: boolean;
}

/** visibility_rule → состояние конструктора (обратный разбор при редактировании). */
export const parseVisibilityRule = (rule?: RetrieveVisibilityRuleSchema | null): VisibilityState => {
  const state: VisibilityState = {};
  if (!rule?.conditions) {
    return state;
  }
  rule.conditions.forEach((cond) => {
    state[cond.attribute] = {
      include: [...(cond.include || [])],
      exclude: [...(cond.exclude || [])],
    };
  });
  return state;
};

const translate = (
  code: string | undefined,
  detail: string | undefined,
  title: string | undefined,
  t: TFunction<Namespace<'validation'>>
): string => {
  if (code) {
    const translated = t(code as any);
    if (translated && translated !== code) {
      return translated;
    }
  }
  return detail || title || code || 'Unknown error';
};

/** Есть ли в разобранной ошибке что показать пользователю. */
export const hasAnyMessage = (e: ParsedAssignmentError): boolean =>
  e.generalErrors.length > 0 || e.ruleErrors.length > 0 || Object.keys(e.fieldErrors).length > 0 || e.isSelfForbidden;

function parseInto(
  errors: IErrorResponse['errors'],
  result: ParsedAssignmentError,
  t: TFunction<Namespace<'validation'>>
): void {
  errors.forEach((e) => {
    const message = translate(e.code, e.detail, e.title, t);
    const pointer = e.source?.pointer || '';
    const segment = pointer.replace(/^\//, '').split('/')[0];
    if ((e.code as string) === SELF_FORBIDDEN_CODE) {
      result.isSelfForbidden = true;
      result.generalErrors.push(message);
    } else if (segment === 'visibility_rule') {
      result.ruleErrors.push(message);
    } else if (segment) {
      result.fieldErrors[segment] = message;
    } else {
      result.generalErrors.push(message);
    }
  });
}

/** Разбирает ошибку бэкенда назначения в удобную для формы структуру. */
export const parseAssignmentError = (err: unknown, t: TFunction<Namespace<'validation'>>): ParsedAssignmentError => {
  const result: ParsedAssignmentError = {
    fieldErrors: {},
    ruleErrors: [],
    generalErrors: [],
    isSelfForbidden: false,
  };
  const axiosErr = err as AxiosError<IErrorResponse>;
  const errors = axiosErr?.response?.data?.errors;
  if (!errors || !errors.length) {
    // Нет структурированных ошибок — вызывающий покажет общий текст.
    return result;
  }
  parseInto(errors, result, t);
  return result;
};

/** Приводит значение к типу атрибута (число для товарных, строка для остальных). */
const coerce = (attribute: string, value: RuleValue): RuleValue => {
  if (isKnownAttribute(attribute) && ATTR_META[attribute].valueType === AttrValueType.Number) {
    return typeof value === 'number' ? value : Number(value);
  }
  return typeof value === 'string' ? value : String(value);
};

/**
 * Состояние конструктора → visibility_rule.
 * Возвращает null, если ни в одном атрибуте нет значений (пустое правило бэкенд отвергает).
 */
export const buildVisibilityRule = (state: VisibilityState): VisibilityRuleSchema | null => {
  const conditions: VisibilityCondition[] = [];
  Object.entries(state).forEach(([attribute, lists]) => {
    const include = (lists.include || []).map((v) => coerce(attribute, v));
    const exclude = (lists.exclude || []).map((v) => coerce(attribute, v));
    if (!include.length && !exclude.length) {
      return;
    }
    conditions.push({
      attribute,
      ...(include.length ? { include } : {}),
      ...(exclude.length ? { exclude } : {}),
    });
  });
  if (!conditions.length) {
    return null;
  }
  return { section: PermissionScope.Orders, conditions };
};
